import { can, formatDate } from '@clim/client-core';
import {
  PROSPECT_OUTCOMES,
  PROSPECT_OUTCOME_LABELS,
  PROSPECT_STATUS_LABELS,
  PROSPECT_STATUS_TONES,
  isoDate,
  normalizePhone,
  prospectToClient,
  statusAfter,
  uuidv7,
  type ProspectOutcome,
  type ProspectRecord,
  type ProspectStatus,
} from '@clim/domain';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClientForm } from '../components/ClientForm';
import { Empty, ErrorBox, Field, Input, Modal, Select, Textarea } from '../components/ui';
import { errorMessage, fieldErrors } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';
import { useOnline } from '../lib/use-online';

const dayFr = (d: string) => d.split('-').reverse().join('/');
export const ProspectBadge = ({ status }: { status: ProspectStatus }) => <span className={`badge tone-${PROSPECT_STATUS_TONES[status]}`}>{PROSPECT_STATUS_LABELS[status]}</span>;

/**
 * Prospects. Commercial : ses contacts à appeler (rappels échus d'abord), « Noter l'appel » (résultat + commentaire
 * + date de rappel, hors ligne), « Créer le client » quand le client a validé. Superviseur : saisie, import,
 * attribution / réattribution, suivi par commercial.
 */
export function ProspectsPage() {
  const { me } = useReady();
  const engine = useEngine();
  const manager = can(me, 'prospect.manage');
  const hasOwn = engine.list('prospect').some((p) => p.assignedToId === me.user.id);
  const [view, setView] = useState<'mine' | 'team'>(manager ? 'team' : 'mine');
  return (
    <div>
      <h1>Prospects</h1>
      {manager && hasOwn && (
        <div className="chips" role="tablist">
          <button role="tab" aria-selected={view === 'team'} className={`chip${view === 'team' ? ' on' : ''}`} onClick={() => setView('team')}>
            Suivi de l’équipe
          </button>
          <button role="tab" aria-selected={view === 'mine'} className={`chip${view === 'mine' ? ' on' : ''}`} onClick={() => setView('mine')}>
            Mes prospects
          </button>
        </div>
      )}
      {view === 'team' && manager ? <TeamView /> : <MineView />}
    </div>
  );
}

/* ------------------------------------------------------------------ commercial */

function MineView() {
  const engine = useEngine();
  const today = isoDate(new Date());
  const [filter, setFilter] = useState<'todo' | 'won' | 'closed' | 'all'>('todo');
  const [contact, setContact] = useState<ProspectRecord | null>(null);
  const [convert, setConvert] = useState<ProspectRecord | null>(null);
  const all = engine.myProspects(today);
  const list = all.filter((p) => (filter === 'todo' ? p.status === 'to_contact' || p.status === 'callback' : filter === 'won' ? p.status === 'won' : filter === 'closed' ? p.status === 'closed' : true));
  const count = (f: (p: ProspectRecord) => boolean) => all.filter(f).length;
  return (
    <div>
      <div className="chips" role="tablist" aria-label="Filtre">
        {(
          [
            ['todo', `À traiter (${count((p) => p.status === 'to_contact' || p.status === 'callback')})`],
            ['won', `Validés (${count((p) => p.status === 'won')})`],
            ['closed', `Clôturés (${count((p) => p.status === 'closed')})`],
            ['all', 'Tous'],
          ] as const
        ).map(([k, label]) => (
          <button key={k} role="tab" aria-selected={filter === k} className={`chip${filter === k ? ' on' : ''}`} onClick={() => setFilter(k)}>
            {label}
          </button>
        ))}
      </div>
      {!list.length && <Empty>{filter === 'todo' ? 'Aucun prospect à contacter. Les contacts attribués par votre superviseur apparaîtront ici.' : 'Aucun prospect.'}</Empty>}
      <ul className="plain prospects">
        {list.map((p) => (
          <ProspectCard key={p.id} p={p} today={today} onContact={() => setContact(p)} onConvert={() => setConvert(p)} />
        ))}
      </ul>
      {contact && <ContactDialog p={contact} onClose={() => setContact(null)} onValidated={(p) => setConvert(p)} />}
      {convert && <ConvertDialog p={convert} onClose={() => setConvert(null)} />}
    </div>
  );
}

function ProspectCard({ p, today, onContact, onConvert }: { p: ProspectRecord; today: string; onContact(): void; onConvert(): void }) {
  const [history, setHistory] = useState(false);
  const due = p.status === 'callback' && p.callbackOn && p.callbackOn <= today;
  const last = p.contacts[0];
  return (
    <li className={`card prospect${due ? ' due' : ''}`} aria-label={`Prospect ${p.name}`}>
      <div className="card-head">
        <h2>{p.name}</h2>
        <ProspectBadge status={p.status} />
      </div>
      <p>
        <a className="btn small primary" href={`tel:${normalizePhone(p.phone)}`} aria-label={`Appeler ${p.name}`}>
          📞 {p.phone}
        </a>
        {p.city && <span className="muted"> · {p.city}</span>}
        {p.source && <span className="muted"> · {p.source}</span>}
      </p>
      {p.comment && (
        <p className="note">
          <strong>Consigne :</strong> {p.comment} <span className="muted">— {p.assignedByName ?? p.createdByName}</span>
        </p>
      )}
      {p.status === 'callback' && p.callbackOn && <p className={due ? 'warn' : 'muted'}>{due ? '⏰ À rappeler aujourd’hui' : `À rappeler le ${dayFr(p.callbackOn)}`}</p>}
      {last && (
        <p className="muted">
          Dernier contact le {formatDate(last.at)} : <strong>{PROSPECT_OUTCOME_LABELS[last.outcome]}</strong>
          {last.comment ? ` — ${last.comment}` : ''}
        </p>
      )}
      <div className="actions">
        {p.status !== 'archived' && (
          <button className="btn small primary" onClick={onContact}>
            Noter l’appel
          </button>
        )}
        {p.status === 'won' && !p.clientId && (
          <button className="btn small" onClick={onConvert}>
            Créer le client
          </button>
        )}
        {p.clientId && (
          <Link className="btn small" to={`/clients/${p.clientId}`}>
            Fiche client
          </Link>
        )}
        {p.contacts.length > 0 && (
          <button className="btn small ghost" onClick={() => setHistory(!history)}>
            Historique ({p.contacts.length})
          </button>
        )}
      </div>
      {history && <History p={p} />}
    </li>
  );
}

function History({ p }: { p: ProspectRecord }) {
  return (
    <ul className="timeline" aria-label={`Historique de ${p.name}`}>
      {p.contacts.map((c) => (
        <li key={c.id}>
          <strong>{PROSPECT_OUTCOME_LABELS[c.outcome]}</strong> <span className="muted">· {formatDate(c.at)} · {c.byName}</span>
          {c.comment && <div>{c.comment}</div>}
          {c.callbackOn && <div className="muted">Rappel prévu le {dayFr(c.callbackOn)}</div>}
        </li>
      ))}
    </ul>
  );
}

function ContactDialog({ p, onClose, onValidated }: { p: ProspectRecord; onClose(): void; onValidated(p: ProspectRecord): void }) {
  const engine = useEngine();
  const [outcome, setOutcome] = useState<ProspectOutcome | ''>('');
  const [comment, setComment] = useState('');
  const [callbackOn, setCallbackOn] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const withCallback = outcome !== '' && statusAfter(outcome) === 'callback';
  const save = async () => {
    if (!outcome) return setError('Choisissez le résultat de l’appel');
    setError(null);
    try {
      await engine.contactProspect(p.id, { outcome, comment, callbackOn: withCallback ? callbackOn || null : null });
      onClose();
      if (outcome === 'validated' && !p.clientId) onValidated(engine.get('prospect', p.id) ?? p);
    } catch (e) {
      setErrors(fieldErrors(e));
      setError(errorMessage(e));
    }
  };
  return (
    <Modal title={`Appel — ${p.name}`} onClose={onClose}>
      <div className="form">
        <fieldset className="outcomes">
          <legend>Résultat</legend>
          {PROSPECT_OUTCOMES.map((o) => (
            <label key={o} className={`outcome${outcome === o ? ' on' : ''}`}>
              <input type="radio" name="outcome" value={o} checked={outcome === o} onChange={() => setOutcome(o)} /> {PROSPECT_OUTCOME_LABELS[o]}
            </label>
          ))}
        </fieldset>
        <Field label={outcome === 'other' ? 'Commentaire (obligatoire)' : 'Commentaire'} error={errors.comment}>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ce que le client a dit, le produit qui l’intéresse, ce qui est convenu…" />
        </Field>
        {withCallback && (
          <Field label="À rappeler le (facultatif)" error={errors.callbackOn}>
            <Input type="date" value={callbackOn} min={isoDate(new Date())} onChange={(e) => setCallbackOn(e.target.value)} />
          </Field>
        )}
        <ErrorBox message={error} />
        <button className="btn primary" onClick={() => void save()}>
          Enregistrer
        </button>
        <p className="muted">Fonctionne hors ligne : l’appel sera transmis à la prochaine synchronisation.</p>
      </div>
    </Modal>
  );
}

function ConvertDialog({ p, onClose }: { p: ProspectRecord; onClose(): void }) {
  const engine = useEngine();
  const navigate = useNavigate();
  return (
    <Modal title={`Créer le client — ${p.name}`} onClose={onClose}>
      <p className="muted">Fiche pré-remplie depuis le prospect : complétez l’adresse puis enregistrez. Vous pourrez ensuite créer le dossier.</p>
      <ClientForm
        territoryId={p.territoryId}
        prefill={prospectToClient(p) as never}
        onCancel={onClose}
        onSaved={(id) => {
          void engine.linkProspectClient(p.id, id).then(() => navigate(`/clients/${id}`));
        }}
      />
    </Modal>
  );
}

/* ------------------------------------------------------------------ superviseur */

interface Assignee {
  id: string;
  name: string;
  territoryIds: string[];
  commercial: boolean;
}

function useAssignees(territoryId: string | null) {
  const { api } = useReady();
  const online = useOnline();
  const [list, setList] = useState<Assignee[]>([]);
  useEffect(() => {
    if (!online) return;
    api.request<Assignee[]>('GET', `/v1/prospects/assignees${territoryId ? `?territoryId=${territoryId}` : ''}`).then(setList, () => setList([]));
  }, [api, online, territoryId]);
  return list;
}

function TeamView() {
  const engine = useEngine();
  const { api, me } = useReady();
  const online = useOnline();
  const { territoryId } = useTerritory();
  const assignees = useAssignees(territoryId);
  const [who, setWho] = useState('');
  const [status, setStatus] = useState<'' | ProspectStatus>('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState('');
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [open, setOpen] = useState<ProspectRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const all = engine.list('prospect').filter((p) => !territoryId || p.territoryId === territoryId);
  const term = q.trim().toLowerCase();
  const list = all
    .filter((p) => (who === '' ? true : who === 'none' ? !p.assignedToId : p.assignedToId === who))
    .filter((p) => (status ? p.status === status : p.status !== 'archived'))
    .filter((p) => !term || [p.name, p.phone, p.city, p.comment].some((v) => v?.toLowerCase().includes(term)))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const summary = useMemo(() => {
    const m = new Map<string, { name: string; counts: Record<ProspectStatus, number> }>();
    for (const p of all) {
      if (!p.assignedToId || p.status === 'archived') continue;
      const e = m.get(p.assignedToId) ?? { name: p.assignedToName ?? '—', counts: { to_contact: 0, callback: 0, won: 0, closed: 0, archived: 0 } };
      e.counts[p.status]++;
      m.set(p.assignedToId, e);
    }
    return [...m.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [all]);

  const assign = async () => {
    setError(null);
    setInfo(null);
    try {
      const r = await api.request<{ assigned: number }>('POST', '/v1/prospects/assign', { ids: [...selected], userId: target === 'none' ? null : target });
      setInfo(`${r.assigned} prospect(s) attribué(s).`);
      setSelected(new Set());
      await engine.sync();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const toggle = (id: string) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };

  return (
    <div>
      <div className="filters">
        <button className="btn primary" onClick={() => setAdding(true)} disabled={!online}>
          + Prospect
        </button>
        <button className="btn" onClick={() => setImporting(true)} disabled={!online}>
          Importer une liste
        </button>
      </div>
      {summary.length > 0 && (
        <table className="table compact" aria-label="Suivi par commercial">
          <thead>
            <tr>
              <th>Commercial</th>
              <th>À contacter</th>
              <th>À rappeler</th>
              <th>Validés</th>
              <th>Clôturés</th>
              <th>Prévenir</th>
            </tr>
          </thead>
          <tbody>
            {summary.map(([id, s]) => (
              <tr key={id} className="clickable" onClick={() => setWho(id)}>
                <td>{s.name}</td>
                <td>{s.counts.to_contact}</td>
                <td>{s.counts.callback}</td>
                <td>{s.counts.won}</td>
                <td>{s.counts.closed}</td>
                <td>
                  <button
                    className="btn small"
                    disabled={!online || s.counts.to_contact + s.counts.callback === 0}
                    aria-label={`Prévenir ${s.name} par SMS`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setError(null);
                      setInfo(null);
                      api
                        .request<{ to: string; count: number }>('POST', '/v1/prospects/sms-reminder', { userId: id })
                        .then(
                          (r) => setInfo(`SMS envoyé à ${r.to} : ${r.count} prospect(s) à appeler, « consultez votre application ».`),
                          (err) => setError(errorMessage(err)),
                        );
                    }}
                  >
                    📱 SMS
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="filters">
        <Input type="search" placeholder="Nom, téléphone, ville…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Rechercher un prospect" />
        <Select value={who} onChange={(e) => setWho(e.target.value)} aria-label="Commercial">
          <option value="">Tous les commerciaux</option>
          <option value="none">Non attribués</option>
          {assignees.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value as ProspectStatus | '')} aria-label="Statut">
          <option value="">Tous (hors archivés)</option>
          {(Object.keys(PROSPECT_STATUS_LABELS) as ProspectStatus[]).map((s) => (
            <option key={s} value={s}>
              {PROSPECT_STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
      </div>
      {selected.size > 0 && (
        <div className="card selection-bar">
          <strong>{selected.size} sélectionné(s)</strong>
          <Select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Attribuer à">
            <option value="">Attribuer à…</option>
            {assignees.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
            <option value="none">Retirer l’attribution</option>
          </Select>
          <button className="btn primary small" disabled={!target || !online} onClick={() => void assign()}>
            Attribuer
          </button>
        </div>
      )}
      <ErrorBox message={error} />
      {info && <p role="status">{info}</p>}
      {!list.length ? (
        <Empty>Aucun prospect. Ajoutez-en un ou importez une liste (copier-coller depuis un tableur).</Empty>
      ) : (
        <table className="table" aria-label="Prospects">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  aria-label="Tout sélectionner"
                  checked={list.length > 0 && list.every((p) => selected.has(p.id))}
                  onChange={(e) => setSelected(e.target.checked ? new Set(list.map((p) => p.id)) : new Set())}
                />
              </th>
              <th>Prospect</th>
              <th>Commercial</th>
              <th>Statut</th>
              <th>Dernier contact</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id}>
                <td>
                  <input type="checkbox" aria-label={`Sélectionner ${p.name}`} checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                </td>
                <td className="clickable" onClick={() => setOpen(p)}>
                  <strong>{p.name}</strong>
                  <div className="muted">
                    {p.phone}
                    {p.city ? ` · ${p.city}` : ''}
                  </div>
                </td>
                <td>{p.assignedToName ?? <span className="muted">Non attribué</span>}</td>
                <td>
                  <ProspectBadge status={p.status} />
                  {p.callbackOn && <div className="muted">rappel {dayFr(p.callbackOn)}</div>}
                </td>
                <td>
                  {p.lastOutcome ? (
                    <>
                      {PROSPECT_OUTCOME_LABELS[p.lastOutcome]}
                      {p.contacts[0]?.comment && <div className="muted">{p.contacts[0].comment}</div>}
                    </>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {adding && <AddDialog assignees={assignees} onClose={() => setAdding(false)} />}
      {importing && <ImportDialog assignees={assignees} onClose={() => setImporting(false)} />}
      {open && <DetailDialog p={engine.get('prospect', open.id) ?? open} canEdit={me.permissions['prospect.manage'] !== undefined} onClose={() => setOpen(null)} />}
    </div>
  );
}

function TerritoryField({ value, onChange }: { value: string; onChange(v: string): void }) {
  const engine = useEngine();
  const { me } = useReady();
  const list = engine
    .list('territory')
    .filter((t) => t.isActive && (me.permissions['prospect.manage'] === 'all' || me.territoryIds.includes(t.id)))
    .sort((a, b) => a.sortOrder - b.sortOrder);
  useEffect(() => {
    if (!value && list.length) onChange(list[0]!.id);
  }, [value, list, onChange]);
  if (list.length < 2) return null;
  return (
    <Field label="Territoire">
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        {list.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function AssigneeField({ assignees, territoryId, value, onChange }: { assignees: Assignee[]; territoryId: string; value: string; onChange(v: string): void }) {
  return (
    <Field label="Attribuer à">
      <Select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Attribuer à">
        <option value="">Plus tard (non attribué)</option>
        {assignees
          .filter((a) => a.territoryIds.includes(territoryId))
          .map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
      </Select>
    </Field>
  );
}

function AddDialog({ assignees, onClose }: { assignees: Assignee[]; onClose(): void }) {
  const { api } = useReady();
  const engine = useEngine();
  const { territoryId: current } = useTerritory();
  const [territoryId, setTerritoryId] = useState(current ?? '');
  const [f, setF] = useState({ name: '', phone: '', city: '', comment: '', source: '' });
  const [assignedToId, setAssignedToId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setError(null);
    try {
      await api.request('POST', '/v1/prospects', { id: uuidv7(), territoryId, ...f, assignedToId: assignedToId || null });
      await engine.sync();
      onClose();
    } catch (e) {
      setErrors(fieldErrors(e));
      setError(errorMessage(e));
    }
  };
  return (
    <Modal title="Nouveau prospect" onClose={onClose}>
      <div className="form">
        <TerritoryField value={territoryId} onChange={setTerritoryId} />
        <Field label="Nom" error={errors.name}>
          <Input value={f.name} onChange={set('name')} placeholder="ex. Mme Laure Hibiscus" />
        </Field>
        <Field label="Téléphone" error={errors.phone}>
          <Input type="tel" value={f.phone} onChange={set('phone')} />
        </Field>
        <Field label="Ville">
          <Input value={f.city} onChange={set('city')} />
        </Field>
        <Field label="Commentaire / consigne pour le commercial">
          <Textarea value={f.comment} onChange={set('comment')} placeholder="ex. intéressé par 2 splits, rappeler après 17 h" />
        </Field>
        <Field label="Origine (facultatif)">
          <Input value={f.source} onChange={set('source')} placeholder="ex. salon, parrainage, site internet" />
        </Field>
        <AssigneeField assignees={assignees} territoryId={territoryId} value={assignedToId} onChange={setAssignedToId} />
        <ErrorBox message={error} />
        <button className="btn primary" onClick={() => void save()} disabled={!f.name.trim() || !f.phone.trim()}>
          Enregistrer
        </button>
      </div>
    </Modal>
  );
}

function ImportDialog({ assignees, onClose }: { assignees: Assignee[]; onClose(): void }) {
  const { api } = useReady();
  const engine = useEngine();
  const { territoryId: current } = useTerritory();
  const [territoryId, setTerritoryId] = useState(current ?? '');
  const [text, setText] = useState('');
  const [source, setSource] = useState('');
  const [assignedToId, setAssignedToId] = useState('');
  const [result, setResult] = useState<{ created: number; duplicates: { name: string; phone: string }[]; errors: { line: number; message: string }[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setError(null);
    try {
      setResult(await api.request('POST', '/v1/prospects/import', { territoryId, text, source: source || null, assignedToId: assignedToId || null }));
      await engine.sync();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <Modal title="Importer une liste de prospects" onClose={onClose}>
      <div className="form">
        <p className="muted">Collez les lignes d’un tableur : Nom ; Téléphone ; Ville ; Commentaire (une ligne par contact). Les numéros déjà connus sont ignorés.</p>
        <TerritoryField value={territoryId} onChange={setTerritoryId} />
        <Field label="Liste">
          <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder={'M. Jean Karu;0690 11 22 33;Le Gosier;rappeler le soir\nMme Line Soufrière;0690 44 55 66;Basse-Terre;'} aria-label="Liste" />
        </Field>
        <Field label="Origine (facultatif)">
          <Input value={source} onChange={(e) => setSource(e.target.value)} placeholder="ex. Salon de l’habitat 2026" />
        </Field>
        <AssigneeField assignees={assignees} territoryId={territoryId} value={assignedToId} onChange={setAssignedToId} />
        <ErrorBox message={error} />
        {result ? (
          <div role="status">
            <p>
              <strong>{result.created}</strong> prospect(s) importé(s).
            </p>
            {result.duplicates.length > 0 && <p className="muted">Déjà connus (ignorés) : {result.duplicates.map((d) => `${d.name} (${d.phone})`).join(', ')}</p>}
            {result.errors.length > 0 && <p className="warn">Lignes non reprises : {result.errors.map((e) => `ligne ${e.line} (${e.message})`).join(', ')}</p>}
            <button className="btn" onClick={onClose}>
              Fermer
            </button>
          </div>
        ) : (
          <button className="btn primary" onClick={() => void run()} disabled={!text.trim()}>
            Importer
          </button>
        )}
      </div>
    </Modal>
  );
}

function DetailDialog({ p, canEdit, onClose }: { p: ProspectRecord; canEdit: boolean; onClose(): void }) {
  const { api } = useReady();
  const engine = useEngine();
  const [comment, setComment] = useState(p.comment ?? '');
  const [error, setError] = useState<string | null>(null);
  const patch = async (body: Record<string, unknown>) => {
    setError(null);
    try {
      await api.request('PATCH', `/v1/prospects/${p.id}`, body);
      await engine.sync();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <Modal title={p.name} onClose={onClose}>
      <dl className="kv">
        <dt>Téléphone</dt>
        <dd>
          <a href={`tel:${normalizePhone(p.phone)}`}>{p.phone}</a>
        </dd>
        {p.city && (
          <>
            <dt>Ville</dt>
            <dd>{p.city}</dd>
          </>
        )}
        <dt>Statut</dt>
        <dd>
          <ProspectBadge status={p.status} />
        </dd>
        <dt>Commercial</dt>
        <dd>{p.assignedToName ?? 'Non attribué'}{p.assignedAt ? ` (depuis le ${formatDate(p.assignedAt)})` : ''}</dd>
        <dt>Saisi par</dt>
        <dd>
          {p.createdByName} · {formatDate(p.createdAt)}
          {p.source ? ` · ${p.source}` : ''}
        </dd>
        {p.clientId && (
          <>
            <dt>Client</dt>
            <dd>
              <Link to={`/clients/${p.clientId}`}>Voir la fiche client</Link>
            </dd>
          </>
        )}
      </dl>
      {canEdit && (
        <div className="form">
          <Field label="Commentaire / consigne">
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
          </Field>
          <div className="actions">
            <button className="btn small" onClick={() => void patch({ comment: comment || null })}>
              Enregistrer le commentaire
            </button>
            <button className="btn small" onClick={() => void patch({ archived: p.status !== 'archived' })}>
              {p.status === 'archived' ? 'Restaurer' : 'Archiver'}
            </button>
          </div>
        </div>
      )}
      <ErrorBox message={error} />
      <h2>Historique des contacts</h2>
      {p.contacts.length ? <History p={p} /> : <Empty>Pas encore contacté.</Empty>}
    </Modal>
  );
}
